'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category: string;
}

interface ServiceFAQProps {
  className?: string;
}

const ServiceFAQ = ({ className = '' }: ServiceFAQProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const faqItems: FAQItem[] = [
    {
      id: 'pricing-1',
      question: '가격은 어떻게 책정되나요?',
      answer: '모든 가격은 투명하게 공개되며, 작업의 복잡도와 소요 시간을 기준으로 책정됩니다. 추가 비용은 사전에 협의 후 진행됩니다.',
      category: 'pricing'
    },
    {
      id: 'pricing-2',
      question: '월간 요금제에서 시간이 초과되면 어떻게 되나요?',
      answer: '월간 포함 시간을 초과하는 경우, 시간당 ₩25,000의 추가 요금이 발생합니다. 사전에 알려드리고 승인 후 진행됩니다.',
      category: 'pricing'
    },
    {
      id: 'service-1',
      question: '어떤 종류의 웹사이트를 지원하나요?',
      answer: 'WordPress, React, Next.js, Vue.js 등 다양한 플랫폼을 지원합니다. 맞춤형 개발 사이트도 분석 후 지원 가능합니다.',
      category: 'service'
    },
    {
      id: 'service-2',
      question: '긴급 상황 시 얼마나 빨리 대응 가능한가요?',
      answer: '연간 계약 고객의 경우 12시간 내, 월간 계약 고객은 24시간 내 대응합니다. 심각한 보안 이슈의 경우 더 빠른 대응이 가능합니다.',
      category: 'service'
    },
    {
      id: 'process-1',
      question: '프로젝트 진행 과정을 어떻게 확인할 수 있나요?',
      answer: '전용 프로젝트 추적 시스템을 통해 실시간으로 진행 상황을 확인할 수 있습니다. 주요 단계마다 알림을 받으실 수 있습니다.',
      category: 'process'
    },
    {
      id: 'process-2',
      question: '수정 요청은 어떻게 하나요?',
      answer: '이메일, 전화, 또는 프로젝트 추적 시스템을 통해 수정 요청을 하실 수 있습니다. 모든 요청은 24시간 내 검토 후 답변드립니다.',
      category: 'process'
    },
    {
      id: 'technical-1',
      question: '백업은 얼마나 자주 하나요?',
      answer: '월간 이상 계약 고객의 경우 매일 자동 백업을 실시하며, 주요 업데이트 전에는 별도 백업을 진행합니다.',
      category: 'technical'
    },
    {
      id: 'technical-2',
      question: '보안 업데이트는 어떻게 관리되나요?',
      answer: '보안 패치는 발표 즉시 검토하여 48시간 내 적용합니다. 중요한 보안 이슈의 경우 즉시 대응합니다.',
      category: 'technical'
    }
  ];

  const categories = [
    { id: 'all', name: '전체', count: faqItems.length },
    { id: 'pricing', name: '가격 정책', count: faqItems.filter(item => item.category === 'pricing').length },
    { id: 'service', name: '서비스', count: faqItems.filter(item => item.category === 'service').length },
    { id: 'process', name: '진행 과정', count: faqItems.filter(item => item.category === 'process').length },
    { id: 'technical', name: '기술 지원', count: faqItems.filter(item => item.category === 'technical').length }
  ];

  const toggleItem = (itemId: string) => {
    if (!isHydrated) return;

    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(itemId)) {
      newOpenItems.delete(itemId);
    } else {
      newOpenItems.add(itemId);
    }
    setOpenItems(newOpenItems);
  };

  const filteredItems = activeCategory === 'all' 
    ? faqItems 
    : faqItems.filter(item => item.category === activeCategory);

  if (!isHydrated) {
    return (
      <section data-component-id="src/app/services/components/ServiceFAQ.tsx:103:6" data-component-path="src/app/services/components/ServiceFAQ.tsx" data-component-line="103" data-component-file="ServiceFAQ.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%7D" className={`py-16 lg:py-24 bg-surface ${className}`}>
        <div data-component-id="src/app/services/components/ServiceFAQ.tsx:104:8" data-component-path="src/app/services/components/ServiceFAQ.tsx" data-component-line="104" data-component-file="ServiceFAQ.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-4xl%20mx-auto%20px-6%20lg%3Apx-8%22%7D" className="max-w-4xl mx-auto px-6 lg:px-8">
          <div data-component-id="src/app/services/components/ServiceFAQ.tsx:105:10" data-component-path="src/app/services/components/ServiceFAQ.tsx" data-component-line="105" data-component-file="ServiceFAQ.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20mb-16%22%7D" className="text-center mb-16">
            <h2 data-component-id="src/app/services/components/ServiceFAQ.tsx:106:12" data-component-path="src/app/services/components/ServiceFAQ.tsx" data-component-line="106" data-component-file="ServiceFAQ.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-3xl%20lg%3Atext-4xl%20font-semibold%20text-primary%20mb-6%22%2C%22textContent%22%3A%22%EC%9E%90%EC%A3%BC%20%EB%AC%BB%EB%8A%94%20%EC%A7%88%EB%AC%B8%22%7D" className="text-3xl lg:text-4xl font-semibold text-primary mb-6">
              자주 묻는 질문
            </h2>
            <p data-component-id="src/app/services/components/ServiceFAQ.tsx:109:12" data-component-path="src/app/services/components/ServiceFAQ.tsx" data-component-line="109" data-component-file="ServiceFAQ.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-lg%20text-text-secondary%22%2C%22textContent%22%3A%22%EC%84%9C%EB%B9%84%EC%8A%A4%EC%97%90%20%EB%8C%80%ED%95%9C%20%EA%B6%81%EA%B8%88%ED%95%9C%20%EC%A0%90%EB%93%A4%EC%9D%84%20%ED%99%95%EC%9D%B8%ED%95%B4%EB%B3%B4%EC%84%B8%EC%9A%94.%22%7D" className="text-lg text-text-secondary">
              서비스에 대한 궁금한 점들을 확인해보세요.
            </p>
          </div>
          <div data-component-id="src/app/services/components/ServiceFAQ.tsx:113:10" data-component-path="src/app/services/components/ServiceFAQ.tsx" data-component-line="113" data-component-file="ServiceFAQ.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-4%22%7D" className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div data-component-id="src/app/services/components/ServiceFAQ.tsx:115:14" data-component-path="src/app/services/components/ServiceFAQ.tsx" data-component-line="115" data-component-file="ServiceFAQ.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-card%20border%20border-border%20p-6%22%7D" key={i} className="bg-card border border-border p-6">
                <div data-component-id="src/app/services/components/ServiceFAQ.tsx:116:16" data-component-path="src/app/services/components/ServiceFAQ.tsx" data-component-line="116" data-component-file="ServiceFAQ.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%20space-y-2%22%7D" className="animate-pulse space-y-2">
                  <div data-component-id="src/app/services/components/ServiceFAQ.tsx:117:18" data-component-path="src/app/services/components/ServiceFAQ.tsx" data-component-line="117" data-component-file="ServiceFAQ.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-4%20bg-interactive%20rounded%20w-3%2F4%22%7D" className="h-4 bg-interactive rounded w-3/4"></div>
                  <div data-component-id="src/app/services/components/ServiceFAQ.tsx:118:18" data-component-path="src/app/services/components/ServiceFAQ.tsx" data-component-line="118" data-component-file="ServiceFAQ.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-3%20bg-interactive%20rounded%20w-1%2F2%22%7D" className="h-3 bg-interactive rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section data-component-id="src/app/services/components/ServiceFAQ.tsx:129:4" data-component-path="src/app/services/components/ServiceFAQ.tsx" data-component-line="129" data-component-file="ServiceFAQ.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%7D" className={`py-16 lg:py-24 bg-surface ${className}`}>
      <div data-component-id="src/app/services/components/ServiceFAQ.tsx:130:6" data-component-path="src/app/services/components/ServiceFAQ.tsx" data-component-line="130" data-component-file="ServiceFAQ.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-4xl%20mx-auto%20px-6%20lg%3Apx-8%22%7D" className="max-w-4xl mx-auto px-6 lg:px-8">
        <div data-component-id="src/app/services/components/ServiceFAQ.tsx:131:8" data-component-path="src/app/services/components/ServiceFAQ.tsx" data-component-line="131" data-component-file="ServiceFAQ.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20mb-16%22%7D" className="text-center mb-16">
          <h2 data-component-id="src/app/services/components/ServiceFAQ.tsx:132:10" data-component-path="src/app/services/components/ServiceFAQ.tsx" data-component-line="132" data-component-file="ServiceFAQ.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-3xl%20lg%3Atext-4xl%20font-semibold%20text-primary%20mb-6%22%2C%22textContent%22%3A%22%EC%9E%90%EC%A3%BC%20%EB%AC%BB%EB%8A%94%20%EC%A7%88%EB%AC%B8%22%7D" className="text-3xl lg:text-4xl font-semibold text-primary mb-6">
            자주 묻는 질문
          </h2>
          <p data-component-id="src/app/services/components/ServiceFAQ.tsx:135:10" data-component-path="src/app/services/components/ServiceFAQ.tsx" data-component-line="135" data-component-file="ServiceFAQ.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-lg%20text-text-secondary%22%2C%22textContent%22%3A%22%EC%84%9C%EB%B9%84%EC%8A%A4%EC%97%90%20%EB%8C%80%ED%95%9C%20%EA%B6%81%EA%B8%88%ED%95%9C%20%EC%A0%90%EB%93%A4%EC%9D%84%20%ED%99%95%EC%9D%B8%ED%95%B4%EB%B3%B4%EC%84%B8%EC%9A%94.%20%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%EB%8D%94%20%EA%B6%81%EA%B8%88%ED%95%9C%20%EC%82%AC%ED%95%AD%EC%9D%B4%20%EC%9E%88%EC%9C%BC%EC%8B%9C%EB%A9%B4%20%EC%96%B8%EC%A0%9C%EB%93%A0%20%EB%AC%B8%EC%9D%98%ED%95%B4%EC%A3%BC%EC%84%B8%EC%9A%94.%22%7D" className="text-lg text-text-secondary">
            서비스에 대한 궁금한 점들을 확인해보세요. 
            더 궁금한 사항이 있으시면 언제든 문의해주세요.
          </p>
        </div>

        {/* Category Filter */}
        <div data-component-id="src/app/services/components/ServiceFAQ.tsx:142:8" data-component-path="src/app/services/components/ServiceFAQ.tsx" data-component-line="142" data-component-file="ServiceFAQ.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-wrap%20justify-center%20gap-2%20mb-12%22%7D" className="flex flex-wrap justify-center gap-2 mb-12">
          {categories.map((category) => (
            <button data-component-id="src/app/services/components/ServiceFAQ.tsx:144:12" data-component-path="src/app/services/components/ServiceFAQ.tsx" data-component-line="144" data-component-file="ServiceFAQ.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22textContent%22%3A%22(%20)%22%7D"
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`px-4 py-2 border-2 font-medium transition-all duration-200 ${
                activeCategory === category.id
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-transparent text-foreground border-border hover:border-accent'
              }`}
            >
              {category.name} ({category.count})
            </button>
          ))}
        </div>

        {/* FAQ Items */}
        <div data-component-id="src/app/services/components/ServiceFAQ.tsx:159:8" data-component-path="src/app/services/components/ServiceFAQ.tsx" data-component-line="159" data-component-file="ServiceFAQ.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-4%22%7D" className="space-y-4">
          {filteredItems.map((item) => (
            <div data-component-id="src/app/services/components/ServiceFAQ.tsx:161:12" data-component-path="src/app/services/components/ServiceFAQ.tsx" data-component-line="161" data-component-file="ServiceFAQ.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-card%20border%20border-border%20hover%3Ashadow-sm%20transition-shadow%20duration-200%22%7D"
              key={item.id}
              className="bg-card border border-border hover:shadow-sm transition-shadow duration-200"
            >
              <button data-component-id="src/app/services/components/ServiceFAQ.tsx:165:14" data-component-path="src/app/services/components/ServiceFAQ.tsx" data-component-line="165" data-component-file="ServiceFAQ.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22w-full%20p-6%20text-left%20flex%20items-center%20justify-between%20hover%3Abg-surface%20transition-colors%20duration-200%22%7D"
                onClick={() => toggleItem(item.id)}
                className="w-full p-6 text-left flex items-center justify-between hover:bg-surface transition-colors duration-200"
              >
                <h3 data-component-id="src/app/services/components/ServiceFAQ.tsx:169:16" data-component-path="src/app/services/components/ServiceFAQ.tsx" data-component-line="169" data-component-file="ServiceFAQ.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22font-semibold%20text-primary%20pr-4%22%7D" className="font-semibold text-primary pr-4">
                  {item.question}
                </h3>
                <Icon data-component-id="src/app/services/components/ServiceFAQ.tsx:172:16" data-component-path="src/app/services/components/ServiceFAQ.tsx" data-component-line="172" data-component-file="ServiceFAQ.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22className%22%3A%22text-text-secondary%20flex-shrink-0%22%7D"
                  name={openItems.has(item.id) ? "ChevronUpIcon" : "ChevronDownIcon"}
                  size={20}
                  className="text-text-secondary flex-shrink-0"
                />
              </button>
              
              {openItems.has(item.id) && (
                <div data-component-id="src/app/services/components/ServiceFAQ.tsx:180:16" data-component-path="src/app/services/components/ServiceFAQ.tsx" data-component-line="180" data-component-file="ServiceFAQ.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22px-6%20pb-6%20border-t%20border-border%22%7D" className="px-6 pb-6 border-t border-border">
                  <p data-component-id="src/app/services/components/ServiceFAQ.tsx:181:18" data-component-path="src/app/services/components/ServiceFAQ.tsx" data-component-line="181" data-component-file="ServiceFAQ.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-text-secondary%20leading-relaxed%20pt-4%22%7D" className="text-text-secondary leading-relaxed pt-4">
                    {item.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        
      </div>
    </section>
  );
};

export default ServiceFAQ;